<template>
  <div class="vip-daily">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>每日会员付费分析</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 工具条：时间范围 -->
    <el-row :gutter="20" class="toolbar">
      <el-col :span="14">
        <el-radio-group v-model="rangeType" @change="onRangeChange" size="small">
          <el-radio-button label="0">当天</el-radio-button>
          <el-radio-button label="1">本周</el-radio-button>
          <el-radio-button label="2">本月</el-radio-button>
          <el-radio-button label="3">自定义</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="rangeType === '3'"
          v-model="customRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          size="small"
          @change="onCustomChange"
          style="margin-left: 12px"
        />
      </el-col>
      <el-col :span="10" style="text-align: right">
        <span class="lbl">指标</span>
        <el-radio-group v-model="metricMode" @change="renderChart" size="small">
          <el-radio-button label="count">笔数</el-radio-button>
          <el-radio-button label="amount">金额</el-radio-button>
          <el-radio-button label="both">笔数+金额</el-radio-button>
        </el-radio-group>
        <el-radio-group v-model="chartMode" @change="renderChart" size="small" style="margin-left: 10px">
          <el-radio-button label="bar">堆叠柱图</el-radio-button>
          <el-radio-button label="percent">百分比</el-radio-button>
          <el-radio-button label="line">折线</el-radio-button>
        </el-radio-group>
      </el-col>
    </el-row>

    <!-- 各类型汇总卡片 -->
    <div class="cards">
      <div
        class="card"
        v-for="t in vipTypes"
        :key="t"
        :style="{ borderTopColor: colorMap[t] }"
      >
        <div class="card-title">{{ t }}</div>
        <template v-if="metricMode === 'both'">
          <div class="card-num" :style="{ color: colorMap[t] }">{{ typeTotals[t] }}<span class="unit"> 笔</span></div>
          <div class="card-amt">¥{{ typeAmountTotals[t].toLocaleString() }}</div>
        </template>
        <template v-else-if="metricMode === 'amount'">
          <div class="card-num" :style="{ color: colorMap[t] }">¥{{ typeAmountTotals[t].toLocaleString() }}</div>
          <div class="card-sub">元 / 当前区间</div>
        </template>
        <template v-else>
          <div class="card-num" :style="{ color: colorMap[t] }">{{ typeTotals[t] }}</div>
          <div class="card-sub">笔 / 当前区间</div>
        </template>
      </div>
    </div>

    <!-- 主图（笔数 / 金额 / 笔数+金额 由 option 内部用 grid 控制） -->
    <div class="chart-card">
      <div class="chart-head">
        <span class="chart-title">每日会员类型付费分析</span>
        <span class="chart-range">区间：{{ dateStart }} ~ {{ dateEnd }}</span>
      </div>
      <div class="cavasbox" ref="chart" v-loading="loading"></div>
    </div>
  </div>
</template>

<script>
import { vipTypeDailyReport } from "../../api/payMG";
import Chart from "echarts";

// 真实会员类型（按产品定义）
const VIP_TYPES = ["天会员", "月会员", "季度会员", "年会员", "99年会员"];
const COLOR_MAP = {
  天会员: "#409EFF",
  月会员: "#67C23A",
  季度会员: "#E6A23C",
  年会员: "#F56C6C",
  "99年会员": "#9B59B6",
};

export default {
  name: "vipTypeDaily",
  data() {
    return {
      vipTypes: VIP_TYPES,
      colorMap: COLOR_MAP,
      loading: false,
      rangeType: "2", // 默认本月
      customRange: null,
      metricMode: "both", // 默认同时展示笔数+金额
      chartMode: "bar", // 堆叠柱图 / 百分比 / 折线
      dateStart: "",
      dateEnd: "",
      dates: [],
      seriesData: {}, // { "天会员":[笔数...], ... }（count）
      amountData: {}, // { "天会员":[金额...], ... }（amount）
      _meta: [], // 与 series 顺序对应的 isAmount 标记，供 tooltip 用
      chart: null,
    };
  },
  computed: {
    typeTotals() {
      const o = {};
      this.vipTypes.forEach((t) => {
        o[t] = (this.seriesData[t] || []).reduce((a, b) => a + (b || 0), 0);
      });
      return o;
    },
    typeAmountTotals() {
      const o = {};
      this.vipTypes.forEach((t) => {
        o[t] = (this.amountData[t] || []).reduce((a, b) => a + (b || 0), 0);
      });
      return o;
    },
  },
  created() {
    this.initRange();
  },
  mounted() {
    this.getReport();
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chart) this.chart.dispose();
  },
  methods: {
    /* ---------- 日期工具 ---------- */
    initRange() {
      const m = this.getCurrentMonth();
      this.dateStart = m[0];
      this.dateEnd = m[1];
    },
    onRangeChange(val) {
      if (val === "0") {
        const d = this.getCurrentDay();
        this.dateStart = d[0];
        this.dateEnd = d[1];
      } else if (val === "1") {
        const w = this.getCurrentWeek();
        this.dateStart = w[0];
        this.dateEnd = w[1];
      } else if (val === "2") {
        const m = this.getCurrentMonth();
        this.dateStart = m[0];
        this.dateEnd = m[1];
      } else if (val === "3" && this.customRange) {
        this.dateStart = this.customRange[0];
        this.dateEnd = this.customRange[1];
      }
      this.getReport();
    },
    onCustomChange(range) {
      if (!range) return;
      this.dateStart = range[0];
      this.dateEnd = range[1];
      this.getReport();
    },
    getCurrentDay() {
      const n = new Date();
      const s = this.fmt(n);
      return [s, s];
    },
    getCurrentWeek() {
      // 近 7 天（含今天）
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 6);
      return [this.fmt(start), this.fmt(now)];
    },
    getCurrentMonth() {
      // 近 30 天（含今天）
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 29);
      return [this.fmt(start), this.fmt(now)];
    },
    fmt(d) {
      const p = (n) => (n < 10 ? "0" + n : "" + n);
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },
    dateRange(begin, end) {
      const arr = begin.split("-");
      const b = new Date(arr[0], arr[1] - 1, arr[2]);
      const e = new Date(end);
      const out = [];
      for (let t = b.getTime(); t <= e.getTime(); t += 86400000) {
        const d = new Date(t);
        out.push(this.fmt(d));
      }
      return out;
    },

    /* ---------- 数据获取 ---------- */
    getReport() {
      this.loading = true;
      vipTypeDailyReport({
        startRq: this.dateStart,
        endRq: this.dateEnd,
        token: localStorage.getItem("logintoken"),
      })
        .then((res) => {
          if (res && res.code === 0 && res.data) {
            const norm = this.normalize(res.data);
            if (norm) {
              this.dates = norm.dates;
              this.seriesData = norm.series;
              this.amountData = norm.amount;
              return;
            }
            this.$message.error("数据格式异常，请检查接口返回结构");
          } else {
            this.$message.error((res && res.msg) || "获取每日会员付费数据失败");
          }
        })
        .catch(() => {
          this.$message.error("网络请求失败，请检查接口连接");
        })
        .finally(() => {
          this.loading = false;
          this.renderChart();
        });
    },
    normalize(data) {
      let dates = [];
      const series = {};
      const amount = {};
      if (data.dates && data.series) {
        dates = data.dates;
        this.vipTypes.forEach((t) => {
          series[t] =
            (data.series && (data.series[t] || (data.types && data.series[data.types.indexOf(t)]))) ||
            [];
          amount[t] =
            (data.amountSeries &&
              (data.amountSeries[t] || (data.types && data.amountSeries[data.types.indexOf(t)]))) ||
            [];
        });
      } else if (Array.isArray(data.list)) {
        const map = {};
        data.list.forEach((row) => {
          if (!map[row.date]) map[row.date] = {};
          map[row.date][row.type] = {
            count: row.count || 0,
            amount: row.amount || 0,
          };
        });
        dates = Object.keys(map).sort();
        this.vipTypes.forEach((t) => {
          series[t] = dates.map((d) => (map[d][t] ? map[d][t].count : 0));
          amount[t] = dates.map((d) => (map[d][t] ? map[d][t].amount : 0));
        });
      } else {
        return null;
      }
      // 补齐长度，避免某些类型数组短于日期
      dates.forEach(() => {
        this.vipTypes.forEach((t) => {
          if (series[t].length < dates.length) series[t].push(0);
          if (amount[t].length < dates.length) amount[t].push(0);
        });
      });
      return { dates, series, amount };
    },
    /* ---------- 图表渲染 ---------- */
    renderChart() {
      if (!this.$refs.chart) return;
      if (!this.chart) this.chart = Chart.init(this.$refs.chart);
      this.chart.setOption(this.buildOption(), true);
    },
    resizeChart() {
      if (this.chart) this.chart.resize();
    },
    mkX(dates, gridIndex, hideLabel) {
      return {
        type: "category",
        gridIndex,
        data: dates,
        axisLabel: {
          color: "#666",
          rotate: dates.length > 15 ? 45 : 0,
          show: !hideLabel,
        },
        axisLine: { lineStyle: { color: "#ccc" } },
      };
    },
    mkY(name, gridIndex, isAmount) {
      return {
        type: "value",
        gridIndex,
        name,
        nameTextStyle: { color: "#999" },
        axisLabel: {
          color: "#666",
          formatter: isAmount ? (v) => "¥" + v : undefined,
        },
        splitLine: { lineStyle: { color: "#eee" } },
      };
    },
    makeSeries(sd, isAmount, gridIndex, yIndex, meta) {
      const types = this.vipTypes;
      const isPercent = this.chartMode === "percent";
      const isLine = this.chartMode === "line";
      const stack = isLine ? null : isPercent ? "pct" + gridIndex : "tot" + gridIndex;
      const totals = this.dates.map((_, i) =>
        types.reduce((s, t) => s + (sd[t] ? sd[t][i] || 0 : 0), 0)
      );
      return types.map((t) => {
        meta.push(isAmount);
        const data = this.dates.map((_, i) => {
          const v = sd[t] ? sd[t][i] || 0 : 0;
          if (isPercent) return +(((v / (totals[i] || 1)) * 100).toFixed(1));
          return v;
        });
        return {
          name: t,
          type: isLine ? "line" : "bar",
          stack: isLine ? undefined : stack,
          xAxisIndex: gridIndex,
          yAxisIndex: yIndex,
          barMaxWidth: 30,
          smooth: isLine,
          showSymbol: isLine && this.dates.length <= 31,
          data,
          itemStyle: { color: this.colorMap[t] },
          lineStyle: isLine ? { color: this.colorMap[t], width: 2 } : undefined,
        };
      });
    },
    fmtTip(params) {
      const isPct = this.chartMode === "percent";
      let s = (params[0] ? params[0].axisValue : "") + "<br/>";
      params.forEach((p) => {
        const isA = this._meta && this._meta[p.seriesIndex];
        const v = p.data;
        let val;
        if (isPct) val = (Number(v) || 0) + "%";
        else val = isA ? "¥" + (Number(v) || 0).toLocaleString() : v + " 笔";
        s += p.marker + p.seriesName + "：" + val + "<br/>";
      });
      return s;
    },
    buildOption() {
      const dates = this.dates;
      const types = this.vipTypes;
      const isLine = this.chartMode === "line";
      const useCount = this.metricMode !== "amount";
      const useAmount = this.metricMode !== "count";
      const meta = [];
      const countSeries = useCount
        ? this.makeSeries(this.seriesData, false, useAmount ? 0 : 0, useAmount ? 0 : 0, meta)
        : [];
      const amountSeries = useAmount
        ? this.makeSeries(this.amountData, true, useAmount && useCount ? 1 : 0, useAmount && useCount ? 1 : 0, meta)
        : [];
      const series = countSeries.concat(amountSeries);
      this._meta = meta;

      const tooltip = {
        trigger: "axis",
        axisPointer: { type: isLine ? "line" : "shadow" },
        formatter: this.fmtTip,
      };
      const legend = { data: types, top: 6 };

      if (useCount && useAmount) {
        // 同时展示：上下两个网格，共用日期轴
        return {
          tooltip,
          legend,
          grid: [
            { left: 64, right: 30, top: 44, bottom: "54%" },
            { left: 64, right: 30, top: "58%", bottom: 40 },
          ],
          xAxis: [this.mkX(dates, 0, true), this.mkX(dates, 1, false)],
          yAxis: [this.mkY("付费笔数", 0, false), this.mkY("付费金额(元)", 1, true)],
          series,
        };
      }
      const isA = useAmount;
      return {
        tooltip,
        legend,
        grid: [{ left: 60, right: 28, top: 46, bottom: 48 }],
        xAxis: [this.mkX(dates, 0, false)],
        yAxis: [this.mkY(isA ? "付费金额(元)" : "付费笔数", 0, isA)],
        series,
      };
    },
  },
};
</script>

<style scoped>
.vip-daily {
  width: 100%;
  box-sizing: border-box;
}
.toolbar {
  margin: 16px 0 4px;
}
.toolbar .lbl {
  font-size: 13px;
  color: #888;
  margin-right: 6px;
}
.cards {
  display: flex;
  gap: 16px;
  margin: 18px 0;
}
.card {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  border-top: 4px solid #409eff;
  padding: 16px 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.card-title {
  font-size: 14px;
  color: #666;
}
.card-num {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 6px;
}
.card-num .unit {
  font-size: 14px;
  font-weight: 400;
  color: #999;
}
.card-amt {
  font-size: 15px;
  color: #333;
  margin-top: 2px;
}
.card-sub {
  font-size: 12px;
  color: #999;
}
.chart-card {
  background: #fff;
  border-radius: 6px;
  padding: 16px 18px 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.chart-title {
  border-left: 5px solid #3bcbff;
  padding-left: 10px;
  font-size: 17px;
  font-weight: 600;
  color: #333;
}
.chart-range {
  font-size: 13px;
  color: #999;
}
.cavasbox {
  width: 100%;
  height: 60vh;
  min-height: 420px;
}
</style>



// WEBPACK FOOTER //
// src/views/charts/vipTypeDaily.vue